import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/atlas/LanguageSwitcher";
import { ArrowLeft, BookOpen, Nfc, ClipboardList, Code, CheckCircle2, ExternalLink } from "lucide-react";

const LOGO_URL = "/atlas-logo.png";

const SECTION_META = [
  { key: "intro", icon: BookOpen, anchor: "intro" },
  { key: "nfc", icon: Nfc, anchor: "nfc" },
  { key: "checklists", icon: ClipboardList, anchor: "checklists" },
  { key: "api", icon: Code, anchor: "api" },
];

export default function Docs() {
  const { t, messages } = useI18n();
  const sections = messages?.docs?.sections || {};

  const toc = useMemo(
    () =>
      SECTION_META.map((m) => ({
        ...m,
        label: t(`docs.nav.${m.key}`),
        title: sections?.[m.key]?.title || "",
      })),
    [sections, t],
  );

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="docs-page"
    >
      <header className="sticky top-0 z-40 bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-3" data-testid="docs-logo" aria-label="ATLAS Industrial">
              <img
                src={LOGO_URL}
                alt="ATLAS Industrial"
                className="h-10 w-auto object-contain select-none"
                draggable={false}
              />
              <span className="hidden sm:inline font-display text-sm text-slate-400 border-l border-slate-200 pl-3 uppercase tracking-[0.18em]">
                Docs
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Link
                to="/"
                data-testid="docs-back-home-btn"
                className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-md text-sm font-semibold text-slate-700 hover:text-[#6B46C1] hover:bg-slate-50"
              >
                <ArrowLeft size={15} />
                {t("docs.backHome")}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">
              <div className="text-xs font-bold tracking-[0.14em] uppercase text-slate-400">
                {t("docs.tocTitle")}
              </div>
              <ul className="mt-4 space-y-1" data-testid="docs-toc">
                {toc.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.key}>
                      <a
                        href={`#${s.anchor}`}
                        data-testid={`docs-toc-${s.key}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-600 hover:text-[#6B46C1] hover:bg-[#6B46C1]/5 transition-colors"
                      >
                        <Icon size={15} className="shrink-0" />
                        {s.label}
                      </a>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 p-4 rounded-xl bg-gradient-to-br from-[#6B46C1]/10 to-purple-50 border border-[#6B46C1]/20">
                <div className="text-sm font-display font-bold text-slate-900">
                  {t("clientLogin")}
                </div>
                <a
                  href="http://app.atlas-system.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-[#6B46C1] font-semibold hover:underline"
                  data-testid="docs-sidebar-app-link"
                >
                  app.atlas-system.net
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </aside>

          <article className="lg:col-span-9 max-w-3xl">
            <div className="pb-8 border-b border-slate-100">
              <h1
                data-testid="docs-title"
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight"
              >
                {t("docs.title")}
              </h1>
              <p className="mt-3 text-slate-500 text-sm sm:text-base">
                {t("docs.tagline")}
              </p>
            </div>

            <DocSection id="intro" icon={BookOpen} data={sections.intro} testid="docs-section-intro">
              <p className="text-slate-700 leading-relaxed">{sections.intro?.p1}</p>
              <p className="mt-4 text-slate-700 leading-relaxed">{sections.intro?.p2}</p>
            </DocSection>

            <DocSection id="nfc" icon={Nfc} data={sections.nfc} testid="docs-section-nfc">
              <p className="text-slate-700 leading-relaxed">{sections.nfc?.p1}</p>
              <StepsList items={sections.nfc?.steps || []} />
            </DocSection>

            <DocSection id="checklists" icon={ClipboardList} data={sections.checklists} testid="docs-section-checklists">
              <p className="text-slate-700 leading-relaxed">{sections.checklists?.p1}</p>
              <StepsList items={sections.checklists?.steps || []} />
            </DocSection>

            <DocSection id="api" icon={Code} data={sections.api} testid="docs-section-api">
              <p className="text-slate-700 leading-relaxed">{sections.api?.p1}</p>
              <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-slate-950 text-slate-100 font-mono text-[13px]">
                {(sections.api?.endpoints || []).map((e, i) => (
                  <div
                    key={i}
                    data-testid={`docs-endpoint-${i}`}
                    className={`flex items-start gap-3 px-4 py-3 ${
                      i > 0 ? "border-t border-slate-800" : ""
                    }`}
                  >
                    <span
                      className={`shrink-0 mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${
                        e.method === "GET"
                          ? "bg-emerald-900/60 text-emerald-300"
                          : "bg-amber-900/60 text-amber-300"
                      }`}
                    >
                      {e.method}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sky-300 break-all">{e.path}</div>
                      <div className="mt-1 text-slate-400 font-sans text-xs">
                        {e.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {sections.api?.note && (
                <p className="mt-4 text-sm text-slate-500 italic">
                  {sections.api.note}
                </p>
              )}
            </DocSection>
          </article>
        </div>
      </main>

      <footer className="border-t border-slate-100 mt-20 py-10 text-center text-xs text-slate-400">
        © 2026 ATLAS Industrial. Hecho en Argentina
      </footer>
    </div>
  );
}

function DocSection({ id, icon: Icon, data, children, testid }) {
  if (!data) return null;
  return (
    <section
      id={id}
      data-testid={testid}
      className="py-10 border-b border-slate-100 last:border-0 scroll-mt-24"
    >
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-[#6B46C1]/10 text-[#6B46C1] flex items-center justify-center">
          <Icon size={20} strokeWidth={1.75} />
        </span>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">
          {data.title}
        </h2>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function StepsList({ items }) {
  if (!items.length) return null;
  return (
    <ol className="mt-5 space-y-3">
      {items.map((s, i) => (
        <li
          key={i}
          className="flex items-start gap-3 text-slate-700"
          data-testid={`docs-step-${i}`}
        >
          <span className="mt-0.5 h-6 w-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 size={14} />
          </span>
          <span className="leading-relaxed">{s}</span>
        </li>
      ))}
    </ol>
  );
}
